import api from "services/api";
import { routers_constants } from "services/constants";

export interface Audience {
  id: number;
  guid: string;
  foreignKey: number;
  placeId: number;
  hearingTypeId: number;
  status: string;
  procedureNumber: string;
  expectedDate: string;
  startDate: null | any;
  finishDate: null | any;
  duration: number;
  description: string;
  userId: number;
  exportCount: number;
  isClassified: boolean | number;
  confidentialityLevel?: number;
  download: boolean;
  extraContent: string;
  notificationDate: string;
  notificationMessage: string;
  active: boolean;
  device: null | any;
  ip: null | any;
  logPoliceUnitId: number;
  logUserId: number;
  recordMode: number;
  hearingTypeName: string;
}

export type IAudienceServices = {
  getListByPlaceId: (data: IParamAudience) => Promise<IReturnAudience>;
  getAllByFilters: (data: IFilterParameters) => Promise<IReturnAudience>;
  getAllByFiltersV2: (data: IFilterParameters) => Promise<IReturnAudience>;
  onMigrateDataToAnotherHearing: (
    currentHearingId: number | string,
    hearingToMigrate: any,
    swapMode?: 0 | 1 | 2,
  ) => Promise<IReturnAudience>;
};

export interface IParamAudience {
  placeId: number;
}

export interface IFilterParameters {
  placeId: number | string;
  district?: boolean;
  procedureNumber?: string;
  startDate?: string;
  finishDate?: string;
  status?: string | null;
  active?: boolean | null;
  hearingTypeId?: number | null;
  attendantId?: number | null;
  globalSearch?: boolean;
  userId?: number | null;
  pageNumber?: number;
  pageSize?: number;
}

export interface IReturnAudience {
  data: any;
  success: boolean;
  totalCount?: number;
  status?: number;
}

export const audiencesServices: IAudienceServices = {
  getListByPlaceId: async ({ placeId }: IParamAudience) => {
    try {
      const { data } = await api.get(
        routers_constants.audiences.getListByPlaceId(placeId),
      );

      return {
        data: data.data,
        success: true,
      };
    } catch (error: any) {
      return {
        data: error?.response?.data,
        success: false,
        status: error?.response?.status,
      };
    }
  },
  getAllByFilters: async (params: IFilterParameters) => {
    try {
      const endpoint = routers_constants.audiences.getAllByFilters;

      const { data } = await api.get(endpoint, {
        params: {
          pageNumber: 1,
          pageSize: 200,
          ...params,
        },
      });

      return {
        data: data.data?.items ?? data.data,
        totalCount: data.data?.totalCount,
        success: true,
      };
    } catch (error: any) {
      return {
        data: error?.response?.data,
        success: false,
        status: error?.response?.status,
      };
    }
  },

  getAllByFiltersV2: async (params: IFilterParameters) => {
    try {
      const endpoint = routers_constants.audiences.getAllByFiltersV2;
      const { data } = await api.get(endpoint, {
        params: {
          pageNumber: 1,
          pageSize: 200,
          ...params,
        },
      });
      return {
        data: data.data?.items ?? data.data,
        totalCount: data.data?.totalCount,
        success: true,
      };
    } catch (error: any) {
      return {
        data: error?.response?.data,
        success: false,
        status: error?.response?.status,
      };
    }
  },
  onMigrateDataToAnotherHearing: async (
    currentHearingId: number | string,
    hearingToMigrate: any,
    swapMode: 0 | 1 | 2 = 0,
  ) => {
    try {
      const endpoint = routers_constants.audiences.migrateDataToAnotherHearing;

      const searchParams = new URLSearchParams({
        hearingId1: String(currentHearingId ?? ""),
        hearingId2: String(hearingToMigrate?.value ?? ""),
        swapMode: String(swapMode),
      });

      const url = `${endpoint}?${searchParams.toString()}`;

      const { data } = await api.post(url, {
        hearingId1: currentHearingId,
        hearingId2: hearingToMigrate.value,
        swapMode,
      });

      return {
        data: data,
        success: true,
      };
    } catch (error: any) {
      return {
        data: error?.response?.data,
        success: false,
      };
    }
  },
};
